(() => {
  const config = document.querySelector('[data-hf-hero-attribution]');
  if (!config || config.dataset.initialized || window.Shopify?.designMode) return;
  config.dataset.initialized = 'true';
  let analyticsIds = new Set();
  let allowlistLoaded = false;
  const keys = ['hf_hero_experiment', 'hf_hero_assigned_variant', 'hf_hero_clicked_variant', 'hf_hero_assigned_at'];
  const assignmentKey = 'hf_hero_assignment_v1';
  const clickedKey = 'hf_hero_clicked_variant';
  const bagKey = 'hf_hero_shuffle_bag_v1';
  let rejected = false;
  let running = null;
  let revision = 0;
  let refresh = false;
  let uncertain = false;
  let handledRevision = 0;
  let privacyRequested = false;
  let privacyTimer;
  let known;
  try { known = extract(JSON.parse(config.textContent)); } catch { return; }

  function privacyState() {
    if (rejected) return false;
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof privacy?.analyticsProcessingAllowed !== 'function') return null;
      const value = privacy.analyticsProcessingAllowed();
      return typeof value === 'boolean' ? value : null;
    } catch { return null; }
  }

  function allowed() { return privacyState() === true; }

  function read(key) {
    try { return JSON.parse(window.sessionStorage.getItem(key)); } catch { return null; }
  }

  function purge() {
    for (const key of [assignmentKey, clickedKey]) {
      try { window.sessionStorage.removeItem(key); } catch {}
    }
    try { window.localStorage.removeItem(bagKey); } catch {}
  }

  function desired() {
    const attrs = Object.fromEntries(keys.map(key => [key, '']));
    if (privacyState() === null) return null;
    if (!allowed()) { purge(); return attrs; }
    const assignment = read(assignmentKey);
    const hero = document.querySelector('hf-hero-distribution');
    if (!assignment || assignment.experiment !== config.dataset.experiment
      || !analyticsIds.has(assignment.variant)
      || typeof assignment.assignedAt !== 'string') return attrs;
    const time = Date.parse(assignment.assignedAt);
    if (!Number.isFinite(time) || time > Date.now() + 60000
      || new Date(time).toISOString() !== assignment.assignedAt) return attrs;
    if (hero && (hero.dataset.experiment !== assignment.experiment
      || hero.dataset.hfConsent !== 'allowed'
      || hero.dataset.hfSelectedVariant !== assignment.variant)) return attrs;
    attrs.hf_hero_experiment = assignment.experiment;
    attrs.hf_hero_assigned_variant = assignment.variant;
    attrs.hf_hero_assigned_at = assignment.assignedAt;
    const clicked = read(clickedKey);
    if (clicked?.experiment === assignment.experiment && clicked?.variant === assignment.variant
      && clicked?.assignedAt === assignment.assignedAt) attrs.hf_hero_clicked_variant = clicked.variant;
    return attrs;
  }

  function extract(attributes) {
    return Object.fromEntries(keys.map(key => [key, typeof attributes?.[key] === 'string' ? attributes[key] : '']));
  }

  function currentIds(root) {
    const heroes = root.querySelectorAll('hf-hero-distribution');
    if (heroes.length !== 1 || heroes[0].dataset.experiment !== config.dataset.experiment) return new Set();
    const patterns = [...heroes[0].querySelectorAll('template[data-hf-hero-pattern]')]
      .filter(pattern => pattern.dataset.valid === 'true');
    const counts = new Map();
    for (const pattern of patterns) {
      const id = pattern.dataset.analyticsId;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    return new Set(patterns.map(pattern => pattern.dataset.analyticsId).filter(id =>
      typeof id === 'string' && /^[a-z0-9][a-z0-9_-]{0,49}$/.test(id)
      && !/[^a-z0-9_-]/.test(id) && counts.get(id) === 1));
  }

  async function loadAllowlist(revalidate) {
    if (config.dataset.pageType === 'index') {
      analyticsIds = currentIds(document);
      return;
    }
    if (allowlistLoaded && !revalidate) return;
    analyticsIds = new Set();
    if (!allowed() || !read(assignmentKey)) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const url = new URL(config.dataset.rootUrl, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid HERO allowlist URL');
      const response = await fetch(url.href, {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal
      });
      if (!response.ok) throw new Error('HERO allowlist unavailable');
      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      analyticsIds = currentIds(markup);
      allowlistLoaded = true;
    } catch {
      allowlistLoaded = false;
    } finally { clearTimeout(timeout); }
  }

  async function request(url, options) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal, ...options });
      if (!response.ok) throw new Error('Hero attribution request failed');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }

  async function drain() {
    let processed;
    do {
      processed = revision;
      handledRevision = processed;
      if (privacyState() === null) { config.dataset.status = 'waiting-api'; return; }
      const revalidate = refresh;
      if (refresh) {
        refresh = false;
        try {
          known = extract((await request(config.dataset.cartUrl)).attributes);
        } catch {
          uncertain = true;
        }
      }
      await loadAllowlist(revalidate);
      const target = desired();
      if (!target) { config.dataset.status = 'waiting-api'; return; }
      if (uncertain || keys.some(key => (known[key] || '') !== target[key])) {
        uncertain = true;
        const cart = await request(config.dataset.updateUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: target })
        });
        known = extract(cart.attributes);
        if (keys.some(key => known[key] !== target[key])) throw new Error('Hero attribution read-back mismatch');
        uncertain = false;
      }
    } while (processed !== revision);
    config.dataset.status = 'synced';
  }

  function sync(readCart = false) {
    if (privacyState() === null) {
      config.dataset.status = 'waiting-api';
      requestPrivacy();
      return Promise.resolve();
    }
    revision++;
    refresh ||= readCart;
    if (!running) {
      config.dataset.status = 'pending';
      running = drain().catch(() => { config.dataset.status = 'error'; }).finally(() => {
        running = null;
        if (handledRevision !== revision) sync();
      });
    }
    return running;
  }

  function consent(event) {
    if (event?.detail?.analyticsAllowed === false) rejected = true;
    else if (event?.type === 'visitorConsentCollected') rejected = false;
    if (privacyState() === false) purge();
    sync(true);
  }

  document.addEventListener('hf:hero:assignment', () => sync());
  document.addEventListener('hf:hero:clicked', () => sync());
  document.addEventListener('product:added-to-cart', () => sync(true));
  document.addEventListener('visitorConsentCollected', consent);
  window.addEventListener('pageshow', event => { if (event.persisted) consent(event); });
  window.addEventListener('focus', consent);

  function requestPrivacy() {
    clearTimeout(privacyTimer);
    privacyTimer = undefined;
    if (privacyState() !== null) { sync(); return; }
    try {
      if (!privacyRequested && typeof window.Shopify?.loadFeatures === 'function') {
        privacyRequested = true;
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], requestPrivacy);
      }
    } catch {}
    if (privacyState() === null && !privacyTimer) privacyTimer = setTimeout(requestPrivacy, 250);
  }
  requestPrivacy();
})();
